import React, { useState, useEffect, useRef } from "react";
import {
  App as AntdApp,
  Card,
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Popconfirm,
  Alert,
  Divider,
  Switch,
  Row,
  Col,
  Modal,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import type { FormInstance } from "antd/es/form";
import type {
  App,
  LandingTemplate,
  StaticSite,
  InlineSiteState,
  LandingMacro,
  CreateStaticSitePayload,
  MappingLike,
  RouteLike,
} from "@/types/api";
import { MacroList } from "../MacroList";
import { StaticSiteStatus } from "@/types/enums";
import { useLandingTemplates, useCreateStaticSiteMutation, useUpdateStaticSiteMutation, useDeleteStaticSiteMutation } from "@/hooks/useAppsQueries";
import { getTplCfg, findApiVsStaticConflicts } from "@/utils/appUtils";
import {
  isPathReserved,
  validatePathSyntax,
  extractApiError,
} from "@/helpers/helpers.app";

const { Text } = Typography;
const { Option } = Select;

const statusColorMap: Record<string, string> = {
  active: "#1890ff",
  pending: "#faad14",
  inactive: "#bfbfbf",
};

function statusColor(s?: string) {
  return statusColorMap[(s || "").toLowerCase()] || "#d9d9d9";
}

export interface LandingCreationContentTabProps {
  form: FormInstance;
  editingApp: App | null;
  isEditMode: boolean;
  isCreateMode: boolean;
  staticSites: StaticSite[];
}

export const LandingCreationContentTab: React.FC<LandingCreationContentTabProps> = ({
  form,
  editingApp,
  isEditMode,
  isCreateMode,
  staticSites,
}) => {
  const { message } = AntdApp.useApp();
  const landingRoutesWatch = Form.useWatch("landingRoutes", form) || [];
  const landingTemplatesQuery = useLandingTemplates();
  const landingTemplates = landingTemplatesQuery.data ?? [];
  const createStaticSiteMutation = useCreateStaticSiteMutation();
  const updateStaticSiteMutation = useUpdateStaticSiteMutation();
  const deleteStaticSiteMutation = useDeleteStaticSiteMutation();

  const prevTplIdsRef = useRef<string[]>([]);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [macrosOpenBySite, setMacrosOpenBySite] = useState<
    Record<string, boolean>
  >({});
  const [inlineSiteState, setInlineSiteState] = useState<
    Record<string, InlineSiteState>
  >({});

  const getInlineState = (siteId: string): InlineSiteState | undefined =>
    inlineSiteState[siteId];

  const setInlineState = (siteId: string, next: Partial<InlineSiteState>) =>
    setInlineSiteState((prev) => ({
      ...prev,
      [siteId]: { ...(prev[siteId] || ({} as InlineSiteState)), ...next },
    }));

  useEffect(() => {
    const routes: any[] = Array.isArray(landingRoutesWatch)
      ? landingRoutesWatch
      : [];
    routes.forEach((route, idx) => {
      const tplId = route?.landingTemplateId as string | undefined;
      const prevTplId = prevTplIdsRef.current[idx];
      const routeEnabled = route?.enabled !== false;
      const tpl = landingTemplates.find((t) => t.id === tplId) || null;
      const { macros: tplMacros = [] } = getTplCfg(tpl) || {};
      const currentMacros: any[] = Array.isArray(route?.macros)
        ? route.macros
        : [];
      if (routeEnabled && tplId) {
        if (prevTplId !== tplId) {
          form.setFieldValue(["landingRoutes", idx, "macros"], tplMacros);
        }
        if (currentMacros.length === 0 && tplMacros.length > 0) {
          form.setFieldValue(["landingRoutes", idx, "macros"], tplMacros);
        }
      }
      prevTplIdsRef.current[idx] = tplId || "";
    });
    if (prevTplIdsRef.current.length > routes.length) {
      prevTplIdsRef.current.length = routes.length;
    }
  }, [landingRoutesWatch, landingTemplates, form]);

  const startEditSite = (site: StaticSite, tpl: LandingTemplate | null) => {
    const macrosFromDB: LandingMacro[] = Array.isArray(site?.macros?.macro)
      ? (site.macros.macro as any)
      : [];
    const { macros: tplMacros } = getTplCfg(tpl || undefined);
    setInlineState(site.id, {
      subdomain: site.subdomain || "",
      basePath: site.basePath || "/",
      status: (site.status as any) || "pending",
      macros: macrosFromDB.length ? macrosFromDB : tplMacros || [],
    });
    setEditingSiteId(site.id);
  };

  const cancelEditSite = (siteId: string) => {
    setInlineSiteState((prev) => {
      const copy = { ...prev };
      delete copy[siteId];
      return copy;
    });
    setEditingSiteId(null);
  };

  const saveSite = async (site: StaticSite) => {
    const st = getInlineState(site.id);
    if (!st) return;
    try {
      let basePath = st.basePath?.trim() || "/";
      if (!basePath.startsWith("/")) basePath = `/${basePath}`;
      if (basePath === "") basePath = "/";

      const endpointsForCheck: MappingLike[] =
        form.getFieldValue("endpointMappings") || [];
      const conflictOnEdit = findApiVsStaticConflicts(
        [
          {
            subdomain: st.subdomain,
            basePath: st.basePath,
            enabled: true,
          } as RouteLike,
        ],
        endpointsForCheck
      );
      if (conflictOnEdit.length) {
        const domainName =
          editingApp?.domain || form.getFieldValue("domain") || "domain.com";
        Modal.error({
          title: "Конфлікт шляхів",
          content: (
            <div>
              Цей шлях вже використовується API:
              <ul style={{ marginTop: 8 }}>
                {conflictOnEdit.map((c: { sub?: string; path: string; api?: string }, i: number) => (
                  <li key={i}>
                    <code>
                      {c.sub ? `${c.sub}.` : ""}
                      {domainName}
                      {c.path}
                    </code>
                    {c.api ? ` (API: ${c.api})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
        return;
      }
      if (isPathReserved(basePath)) {
        Modal.error({
          title: "Шлях зайнято системою",
          content: (
            <div>
              <div>Ви обрали зарезервований системою шлях.</div>
              <div style={{ marginTop: 8 }}>
                <code>{basePath}</code>
              </div>
            </div>
          ),
        });
        return;
      }

      await updateStaticSiteMutation.mutateAsync({
        id: site.id,
        appId: editingApp!.id,
        data: {
          subdomain: st.subdomain?.trim() || null,
          basePath,
          status: st.status,
          siteName: (st?.siteName ?? site.siteName ?? "").trim(),
          macros: Array.isArray(st.macros) ? st.macros : [],
        },
      });
      message.success("Лендінг оновлено");
      setEditingSiteId(null);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Не вдалося зберегти зміни";
      Modal.error({ title: "Помилка збереження", content: String(msg) });
    }
  };

  const handleDeleteStaticSite = async (staticSiteId: string) => {
    if (!editingApp) return;
    try {
      await deleteStaticSiteMutation.mutateAsync({ id: staticSiteId, appId: editingApp.id });
      message.success("Лендінг успішно видалено");
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Не вдалося видалити лендінг";
      Modal.error({ title: "Помилка видалення", content: String(msg) });
    }
  };

  const createStaticFromRoute = async (routeIndex: number) => {
    const route = (form.getFieldValue("landingRoutes") || [])[routeIndex] || {};
    if (!editingApp) {
      Modal.error({
        title: "Спочатку збережіть застосунок (або відкрийте в режимі редагування)",
        content:
          "Збережіть базову інформацію або відкрийте застосунок у режимі редагування.",
      });
      return;
    }
    if (!route.name || !route.name.trim()) {
      Modal.error({
        title: "Некоректні дані",
        content: "Вкажіть назву лендінгу",
      });
      return;
    }
    if (!route.landingTemplateId) {
      Modal.error({ title: "Оберіть шаблон лендингу" });
      return;
    }

    let basePath: string = (route.basePath || "/").trim();
    if (!basePath.startsWith("/")) basePath = `/${basePath}`;
    if (basePath === "") basePath = "/";

    const syntaxErr = validatePathSyntax(basePath);
    if (syntaxErr) {
      Modal.error({ title: "Некоректний шлях", content: syntaxErr });
      return;
    }
    if (isPathReserved(basePath)) {
      Modal.error({
        title: "Шлях зайнято системою",
        content: (
          <div>
            <div>Ви обрали зарезервований системою шлях.</div>
            <div style={{ marginTop: 8 }}>
              <code>{basePath}</code>
            </div>
          </div>
        ),
      });
      return;
    }

    const subdomain: string | undefined =
      route.subdomain?.trim() || undefined;

    const routesProbe = [{ basePath, subdomain }];
    const conflicts = findApiVsStaticConflicts(
      routesProbe as any,
      (form.getFieldValue("endpointMappings") || []) as any
    );
    if (conflicts.length) {
      const domainName =
        editingApp?.domain ||
        form.getFieldValue("domain") ||
        "domain.com";
      Modal.error({
        title: "Конфлікт шляхів",
        content: (
          <div>
            <ul style={{ marginTop: 8 }}>
              {conflicts.map((c: { sub?: string; path: string; api?: string }, i: number) => (
                <li key={i}>
                  <code>
                    {c.sub ? `${c.sub}.` : ""}
                    {domainName}
                    {c.path}
                  </code>
                  {c.api ? ` (API: ${c.api})` : ""}
                </li>
              ))}
            </ul>
          </div>
        ),
      });
      return;
    }

    const key = `${(subdomain || "").trim().toLowerCase()}::${basePath}`;
    const dup = staticSites.some(
      (s) =>
        `${(s.subdomain || "").trim().toLowerCase()}::${s.basePath}` === key
    );
    if (dup) {
      Modal.error({
        title: "Дублікат шляху",
        content: "Такий шлях уже використовується іншим лендінгом.",
      });
      return;
    }

    try {
      const payload: CreateStaticSitePayload = {
        appId: editingApp.id,
        siteName: route.name.trim(),
        landingTemplateId: route.landingTemplateId,
        domain: editingApp.domain || form.getFieldValue("domain"),
        subdomain,
        status: route.enabled
          ? StaticSiteStatus.ACTIVE
          : StaticSiteStatus.INACTIVE,
        basePath,
        macros: Array.isArray(route.macros) ? route.macros : [],
      };

      await createStaticSiteMutation.mutateAsync(payload);
      message.success("Лендінг створено");

      const current: any[] = form.getFieldValue("landingRoutes") || [];
      current.splice(routeIndex, 1);
      form.setFieldsValue({ landingRoutes: current });
    } catch (e: any) {
      console.error(e);
      Modal.error({
        title: "Помилка створення лендінгу",
        content: extractApiError(e),
      });
    }
  };

  return (
    <div>
      <Divider orientation="left">
        <Space>
          <FileOutlined />
          Роутинг лендингів
        </Space>
      </Divider>

      {!!editingApp && (
        <Alert
          message="Налаштування роутингу лендингів"
          description="Тут налаштовується на які ендпоінти та субдомени будуть доступні ваші лендинги"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {(isEditMode || isCreateMode) && (
        <Form.List name="landingRoutes">
          {(fields, { add, remove }) => (
            <>
              <div style={{ marginBottom: 16 }}>
                {editingApp ? (
                  <Button
                    type="dashed"
                    htmlType="button"
                    icon={<PlusOutlined />}
                    onClick={(e) => {
                      e.preventDefault();
                      add({ enabled: true, basePath: "/" });
                    }}
                    style={{ width: "100%" }}
                  >
                    Додати лендинг
                  </Button>
                ) : (
                  <Alert
                    type="info"
                    showIcon
                    message="Спочатку створіть застосунок"
                    description="Збережіть базову інформацію на вкладці «Основне» (назва, шаблон, домен), після цього ви зможете додавати лендінги."
                  />
                )}
              </div>

              {fields.map((field) => {
                const route = landingRoutesWatch?.[field.name] || {};
                const tplId: string | undefined = route?.landingTemplateId;
                const subdomain: string | undefined = route?.subdomain;
                const basePath: string = route?.basePath || "/";
                const isEnabled = !!landingRoutesWatch?.[field.name]?.enabled;
                const domainPreview =
                  editingApp?.domain ||
                  form.getFieldValue("domain") ||
                  "domain.com";

                return (
                  <Card
                    key={field.key}
                    size="small"
                    style={{ marginBottom: 12 }}
                    extra={
                      <Space>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => createStaticFromRoute(field.name)}
                          disabled={!editingApp}
                        >
                          Створити
                        </Button>
                        <Popconfirm
                          title="Видалити цей лендінг?"
                          okText="Так"
                          cancelText="Ні"
                          placement="left"
                          onConfirm={() => remove(field.name)}
                        >
                          <Button danger icon={<DeleteOutlined />}>
                            Видалити
                          </Button>
                        </Popconfirm>
                      </Space>
                    }
                  >
                    <div style={{ width: "100%" }}>
                      <div
                        style={{
                          background: "#fafafa",
                          border: "1px solid #f0f0f0",
                          borderRadius: 10,
                          padding: "14px 18px",
                          marginBottom: 16,
                          fontWeight: 600,
                          color: "#595959",
                          width: "100%",
                        }}
                      >
                        <Row gutter={[24, 0]}>
                          <Col span={4}>Статус</Col>
                          <Col span={6}>Назва</Col>
                          <Col span={6}>Шаблон</Col>
                          <Col span={4}>Шлях (basePath)</Col>
                          <Col span={4}>Сабдомен</Col>
                        </Row>
                      </div>
                      <Row gutter={[24, 20]} align="middle">
                        <Col span={4}>
                          <Form.Item
                            name={[field.name, "enabled"]}
                            valuePropName="checked"
                            style={{
                              minWidth: 180,
                              marginRight: 16,
                            }}
                          >
                            <Space direction="vertical" align="center">
                              <Switch
                                checked={isEnabled}
                                size="small"
                                onChange={(checked) =>
                                  form.setFieldValue(
                                    ["landingRoutes", field.name, "enabled"],
                                    checked
                                  )
                                }
                              />
                              {isEnabled ? (
                                <Tag color="green">Активний</Tag>
                              ) : (
                                <Tag color="default">Вимкнений</Tag>
                              )}
                            </Space>
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            name={[field.name, "name"]}
                            rules={[
                              {
                                required: true,
                                message: "Вкажіть назву лендінгу",
                              },
                              { max: 80, message: "Максимум 80 символів" },
                            ]}
                          >
                            <Input
                              placeholder="Мій лендинг"
                              disabled={!isEnabled}
                              onBlur={(e) => {
                                const v = (e.target.value || "")
                                  .trim()
                                  .replace(/\s+/g, " ");
                                form.setFieldValue(
                                  ["landingRoutes", field.name, "name"],
                                  v
                                );
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            name={[field.name, "landingTemplateId"]}
                            rules={[
                              {
                                required: true,
                                message: "Виберіть шаблон лендингу",
                              },
                            ]}
                            style={{ minWidth: 280 }}
                          >
                            <Select
                              placeholder="Виберіть шаблон лендингу"
                              disabled={!isEnabled}
                              showSearch
                              optionFilterProp="children"
                              allowClear
                            >
                              {landingTemplates.map((t) => (
                                <Option key={t.id} value={t.id}>
                                  {t.name}{" "}
                                  {t.category ? ` — ${t.category}` : ""}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item name={[field.name, "basePath"]}>
                            <Input
                              disabled={!isEnabled}
                              placeholder="/"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item name={[field.name, "subdomain"]}>
                            <Input
                              placeholder="promo"
                              addonAfter={`.${domainPreview}`}
                              disabled={!isEnabled}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <div style={{ marginTop: -8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          URL:{" "}
                          <Text code>
                            https://
                            {subdomain ? `${subdomain}.` : ""}
                            {domainPreview}
                            {basePath?.startsWith("/")
                              ? basePath
                              : `/${basePath}`}
                          </Text>
                        </Text>
                      </div>

                      <Divider
                        orientation="left"
                        style={{ marginTop: 12, marginBottom: 8 }}
                      >
                        Налаштування (макроси)
                      </Divider>
                      {tplId ? (
                        <div style={{ width: "100%" }}>
                          <Form.List name={[field.name, "macros"]}>
                            {(macroFields) => (
                              <>
                                {macroFields.length === 0 ? (
                                  <Alert
                                    message="У вибраному шаблоні немає макросів."
                                    type="warning"
                                    showIcon
                                  />
                                ) : (
                                  <div
                                    style={{
                                      maxHeight: 400,
                                      overflowY: "auto",
                                    }}
                                  >
                                    {macroFields.map((mf, idx) => (
                                      <Card
                                        key={mf.key}
                                        size="small"
                                        style={{ marginBottom: 12 }}
                                        title={
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                            }}
                                          >
                                            <span>Макрос #{idx + 1}</span>
                                          </div>
                                        }
                                      >
                                        <Row gutter={[12, 12]}>
                                          <Col span={12}>
                                            <label>
                                              <strong>
                                                Назва змінної:
                                              </strong>
                                            </label>
                                            <Form.Item
                                              name={[mf.name, "name"]}
                                              rules={[
                                                {
                                                  required: true,
                                                  message:
                                                    "Вкажіть назву змінної",
                                                },
                                              ]}
                                              style={{
                                                marginTop: 4,
                                                marginBottom: 0,
                                              }}
                                            >
                                              <Input
                                                placeholder="COMPANY_NAME"
                                                disabled
                                                readOnly
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col span={12}>
                                            <label>
                                              <strong>
                                                Відображувана назва:
                                              </strong>
                                            </label>
                                            <Form.Item
                                              name={[mf.name, "label"]}
                                              style={{
                                                marginTop: 4,
                                                marginBottom: 0,
                                              }}
                                            >
                                              <Input placeholder="Назва компанії" />
                                            </Form.Item>
                                          </Col>

                                          <Col span={8}>
                                            <label>
                                              <strong>Тип:</strong>
                                            </label>
                                            <Form.Item
                                              name={[mf.name, "type"]}
                                              style={{
                                                marginTop: 4,
                                                marginBottom: 0,
                                              }}
                                            >
                                              <Select>
                                                <Select.Option value="string">
                                                  Текст
                                                </Select.Option>
                                                <Select.Option value="file">
                                                  Файл
                                                </Select.Option>
                                              </Select>
                                            </Form.Item>
                                          </Col>

                                          <Col span={8}>
                                            <label>
                                              <strong>
                                                Обов'язковий:
                                              </strong>
                                            </label>
                                            <Form.Item
                                              name={[mf.name, "required"]}
                                              valuePropName="checked"
                                              style={{
                                                marginTop: 8,
                                                marginBottom: 0,
                                              }}
                                            >
                                              <Switch />
                                            </Form.Item>
                                          </Col>

                                          <Col span={8}>
                                            <Form.Item
                                              noStyle
                                              shouldUpdate
                                            >
                                              {({ getFieldValue }) => {
                                                const type = getFieldValue([
                                                  "landingRoutes",
                                                  field.name,
                                                  "macros",
                                                  mf.name,
                                                  "type",
                                                ]);
                                                if (type === "string") {
                                                  return (
                                                    <>
                                                      <label>
                                                        <strong>
                                                          Значення за
                                                          замовчуванням:
                                                        </strong>
                                                      </label>
                                                      <Form.Item
                                                        name={[
                                                          mf.name,
                                                          "defaultValue",
                                                        ]}
                                                        style={{
                                                          marginTop: 4,
                                                          marginBottom: 0,
                                                        }}
                                                      >
                                                        <Input placeholder="Стандартне значення" />
                                                      </Form.Item>
                                                    </>
                                                  );
                                                }
                                                return null;
                                              }}
                                            </Form.Item>
                                          </Col>

                                          <Col span={24}>
                                            <label>
                                              <strong>Опис:</strong>
                                            </label>
                                            <Form.Item
                                              name={[
                                                mf.name,
                                                "description",
                                              ]}
                                              style={{
                                                marginTop: 4,
                                                marginBottom: 0,
                                              }}
                                            >
                                              <Input.TextArea
                                                rows={2}
                                                placeholder="Опис призначення цього макросу"
                                              />
                                            </Form.Item>
                                          </Col>
                                        </Row>
                                      </Card>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </Form.List>
                        </div>
                      ) : (
                        <Alert
                          message="Оберіть шаблон, щоб налаштувати макроси"
                          type="warning"
                          showIcon
                        />
                      )}
                    </div>
                  </Card>
                );
              })}
            </>
          )}
        </Form.List>
      )}

      <Divider orientation="left">
        <Space>
          <FileOutlined />
          Існуючі лендінги
        </Space>
      </Divider>

      {staticSites.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="Поки що немає створених лендінгів"
        />
      ) : (
        <Space direction="vertical" style={{ width: "100%" }} size={24}>
          {staticSites.map((site) => {
            const tpl =
              landingTemplates.find(
                (t) => t.id === site.landingTemplateId
              ) || null;
            const disabled = editingSiteId !== site.id;
            const st = getInlineState(site.id);

            const subdomainVal = st?.subdomain ?? (site.subdomain || "");
            const basePathVal = st?.basePath ?? (site.basePath || "/");
            const nameVal = st?.siteName ?? (site.siteName || "");
            const statusVal =
              st?.status ?? ((site.status as any) || "pending");
            const macrosVal: LandingMacro[] =
              st?.macros ??
              (Array.isArray(site?.macros?.macro)
                ? (site.macros.macro as any)
                : []);
            const macroCount = macrosVal.length;
            const macrosOpen = macrosOpenBySite[site.id] ?? true;
            const toggleMacros = () =>
              setMacrosOpenBySite((prev) => ({
                ...prev,
                [site.id]: !macrosOpen,
              }));

            return (
              <Card
                key={site.id}
                size="small"
                style={{
                  marginBottom: 8,
                  borderRadius: 12,
                  border: "1px solid #f0f0f0",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                  borderLeft: `6px solid ${statusColor(statusVal)}`,
                }}
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography.Title level={5} style={{ margin: 0 }}>
                      {nameVal || "Без назви"}
                    </Typography.Title>
                    <Typography.Text type="secondary">
                      {tpl
                        ? `Template(${tpl.name}${tpl.category ? ` — ${tpl.category}` : ""})`
                        : "Шаблон"}
                    </Typography.Text>
                  </div>
                }
                extra={
                  <Space>
                    {editingSiteId === site.id ? (
                      <>
                        <Button
                          type="primary"
                          onClick={() => saveSite(site)}
                        >
                          Зберегти
                        </Button>
                        <Button onClick={() => cancelEditSite(site.id)}>
                          Скасувати
                        </Button>
                      </>
                    ) : (
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => startEditSite(site, tpl)}
                      >
                        Редагувати
                      </Button>
                    )}

                    <Popconfirm
                      title="Видалити лендінг?"
                      onConfirm={() => handleDeleteStaticSite(site.id)}
                      okText="Так"
                      cancelText="Ні"
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        Видалити
                      </Button>
                    </Popconfirm>
                  </Space>
                }
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Row gutter={[12, 12]}>
                    <Col span={8}>
                      <label>
                        <strong>Назва</strong>
                      </label>
                      <Input
                        value={nameVal}
                        disabled={disabled}
                        onChange={(e) =>
                          setInlineState(site.id, {
                            siteName: e.target.value,
                          })
                        }
                        onBlur={(e) =>
                          setInlineState(site.id, {
                            siteName: (e.target.value || "")
                              .trim()
                              .replace(/\s+/g, " "),
                          })
                        }
                        maxLength={80}
                        placeholder="Мій лендинг"
                      />
                    </Col>
                    <Col span={8}>
                      <label>
                        <strong>Сабдомен</strong>
                      </label>
                      <Input
                        value={subdomainVal}
                        disabled={disabled}
                        onChange={(e) =>
                          setInlineState(site.id, {
                            subdomain: e.target.value,
                          })
                        }
                        placeholder="promo"
                        addonAfter={`.${editingApp?.domain || form.getFieldValue("domain") || "domain.com"}`}
                      />
                    </Col>

                    <Col span={8}>
                      <label>
                        <strong>Шлях (basePath)</strong>
                      </label>
                      <Input
                        value={basePathVal}
                        disabled={disabled}
                        onChange={(e) =>
                          setInlineState(site.id, {
                            basePath: e.target.value,
                          })
                        }
                        placeholder="/"
                      />
                    </Col>

                    <Col span={8}>
                      <label>
                        <strong>Статус</strong>
                      </label>
                      <Select
                        style={{ width: "100%" }}
                        value={statusVal}
                        disabled={disabled}
                        onChange={(v) =>
                          setInlineState(site.id, {
                            status: v as any,
                          })
                        }
                        options={[
                          {
                            value: StaticSiteStatus.PENDING,
                            label: "pending",
                          },
                          {
                            value: StaticSiteStatus.ACTIVE,
                            label: "active",
                          },
                          {
                            value: StaticSiteStatus.INACTIVE,
                            label: "inactive",
                          },
                        ]}
                      />
                    </Col>
                  </Row>

                  <div style={{ marginTop: 8 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      URL:{" "}
                      <Typography.Text code>
                        https://
                        {subdomainVal ? `${subdomainVal}.` : ""}
                        {editingApp?.domain ||
                          form.getFieldValue("domain") ||
                          "domain.com"}
                        {basePathVal?.startsWith("/")
                          ? basePathVal
                          : `/${basePathVal}`}
                      </Typography.Text>
                    </Typography.Text>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginTop: 12,
                      marginBottom: 8,
                    }}
                  >
                    <Divider
                      orientation="left"
                      style={{ marginTop: 0, flex: 1, minWidth: 0 }}
                    >
                      Налаштування (макроси)
                    </Divider>
                    <Button
                      size="small"
                      style={{ flexShrink: 0 }}
                      type="text"
                      icon={
                        macrosOpen ? (
                          <UpOutlined />
                        ) : (
                          <DownOutlined />
                        )
                      }
                      onClick={toggleMacros}
                    >
                      {macrosOpen ? "Приховати" : "Показати"} макроси
                      {!macrosOpen ? ` (${macroCount})` : ""}
                    </Button>
                  </div>
                  {macrosOpen ? (
                    <MacroList
                      value={macrosVal}
                      disabled={disabled}
                      onChange={(next) =>
                        setInlineState(site.id, { macros: next })
                      }
                    />
                  ) : (
                    <Typography.Text
                      type="secondary"
                      style={{ display: "block", marginTop: 4 }}
                    >
                      Макросів: <b>{macroCount}</b>
                    </Typography.Text>
                  )}
                </Space>
              </Card>
            );
          })}
        </Space>
      )}
    </div>
  );
};
